import { api } from './config';
import { createFormData } from './helpers';
import {
  ApiResponse,
  SampleRequest,
  BatchSampleRequestCreateRequest,
  SampleRequestVersion,
  VersionData
} from '../types';

export const sampleRequestsApi = {
  create: (data: Record<string, any>) => {
    const formData = createFormData(data);
    return api.post<ApiResponse>('/sample-requests', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  track: (requestCode: string) =>
    api.get<ApiResponse>(`/sample-requests/track/${requestCode}`),

  getMyRequests: () =>
    api.get<ApiResponse<SampleRequest[]>>('/sample-requests/my-requests'),

  getMyLatest: (params?: { page?: number; size?: number }) =>
    api.get<ApiResponse<SampleRequest[]>>('/sample-requests/my-latest', { params }),

  getDetail: (id: string | number) =>
    api.get<ApiResponse>(`/sample-requests/${id}`),

  update: (id: string | number, data: Record<string, any>) => {
    const formData = createFormData(data);
    return api.put<ApiResponse>(`/sample-requests/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Batch sample requests
  createBatch: (data: BatchSampleRequestCreateRequest) =>
    api.post<ApiResponse<SampleRequest[]>>('/sample-requests/batch', data),

  getBatchGroup: (batchGroupId: number) =>
    api.get<ApiResponse<SampleRequest[]>>(`/sample-requests/batch/${batchGroupId}`),

  getByProject: (projectId: number, params?: { page?: number; size?: number }) =>
    api.get<ApiResponse<SampleRequest[]>>(`/sample-requests/project/${projectId}`, { params }),

  // Version management
  getVersions: (id: string | number) =>
    api.get<ApiResponse<SampleRequestVersion[]>>(`/sample-requests/${id}/versions`),

  getVersionData: (id: string | number, version: number) =>
    api.get<ApiResponse<VersionData>>(`/sample-requests/${id}/versions/${version}`),

  getAllVersionsById: (id: string | number) =>
    api.get<ApiResponse>(`/sample-requests/${id}/all-versions`),

  // Project management
  moveToProject: (id: string | number, projectId: number | null) =>
    api.put<ApiResponse<SampleRequest>>(`/sample-requests/${id}/move-to-project`, { projectId }),

  // Draft management
  saveDraft: (data: FormData) =>
    api.post<ApiResponse<SampleRequest>>('/sample-requests/draft', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  getMyDrafts: () =>
    api.get<ApiResponse<SampleRequest[]>>('/sample-requests/my-drafts'),

  deleteDraft: (draftId: number) =>
    api.delete<ApiResponse>(`/sample-requests/draft/${draftId}`),

  // Brand soft delete of a published (non-draft) sample request (휴지통 이동, admin 복원 가능)
  softDelete: (requestId: number) =>
    api.delete<ApiResponse>(`/sample-requests/${requestId}`),

  submitDraft: (draftId: number) =>
    api.post<ApiResponse<SampleRequest>>(`/sample-requests/submit/${draftId}`),

  // 여러 draft 를 단일 프로젝트(기존 project_id 또는 신규 project_name)로 일괄 제출
  submitBatchDrafts: (data: { draft_ids: number[]; project_id?: number; project_name?: string }) =>
    api.post<ApiResponse<Record<string, unknown>>>('/sample-requests/submit-batch', data),

  // Save update as draft (for editing existing requests)
  saveUpdateAsDraft: (id: number, data: FormData) =>
    api.post<ApiResponse<SampleRequest>>(`/sample-requests/${id}/save-draft`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Submit a saved draft as a new version
  submitSavedDraft: (draftId: number) =>
    api.post<ApiResponse<SampleRequest>>(`/sample-requests/submit-saved-draft/${draftId}`),

  // Cancel a sample request
  cancel: (requestId: number, reason?: string) =>
    api.post<ApiResponse<SampleRequest>>(`/sample-requests/cancel/${requestId}`, { reason }),
};
